import { INLINES } from '@contentful/rich-text-types'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { useTranslation } from 'next-i18next'

import { ZipCodeButton } from '../../atoms'

const CountdownElement = ({ innerText, classNames }: { innerText?: string; classNames: string[] }) => {
  if (!innerText) return null
  const END_OF_TIME = /end-time-epoch--\w+/g
  const [, endOfTime] = classNames.find(className => className.match(END_OF_TIME))?.split('--') || []

  return <count-down end-time-epoch={endOfTime} template={innerText}></count-down>
}

const FlipWordElement = ({ innerText }: { innerText?: string; classNames: string[] }) => {
  if (!innerText) return null
  const wordsToFlip = innerText.split(' ')

  return (
    <span className="flipWordsWrapper">
      {wordsToFlip.map((wordToFlip, index) => (
        <>
          <span className="flipWord">{wordToFlip}</span>
          {index < wordsToFlip.length - 1 && <span className="flipWordSpace"> </span>}
        </>
      ))}
    </span>
  )
}

const ZipCodeElement = ({ innerText }: { innerText?: string; classNames: string[] }) => {
  const { t } = useTranslation('common')
  const { zipCode } = useZipCode()
  if (!innerText) return null

  const buttonText = zipCode ? zipCode : t('enter_your_zip')
  const [firstString = '', secondString = ''] = innerText.split('{{ZIP}}')
  return (
    <>
      {firstString}
      {zipCode ? ' to ' : '. '}
      <ZipCodeButton>{buttonText}</ZipCodeButton>
      {secondString}
    </>
  )
}

enum ENTRY_TYPES {
  countdown = 'countdown',
  flip_word = 'flip_word',
  zip_code = 'zip_code',
}

const ENTRY_TYPE_VS_RENDERER = {
  [ENTRY_TYPES.countdown]: CountdownElement,
  [ENTRY_TYPES.flip_word]: FlipWordElement,
  [ENTRY_TYPES.zip_code]: ZipCodeElement,
}

export const options = {
  renderNode: {
    [INLINES.EMBEDDED_ENTRY]: (element: any) => {
      if (element?.nodeType === 'embedded-entry-inline') {
        const embeddedEntryData = element?.data?.target

        const { baseDynamicValueClasses, content } = embeddedEntryData
        const classNames: string[] = baseDynamicValueClasses || []
        const innerText = content?.content?.[0]?.content
          ?.map((c: { value: string }) => c.value)
          .join('')
          .trim()

        const ENTRY_TYPE = /type--\w+/g

        const [, entryType] = classNames.find(className => className.match(ENTRY_TYPE))?.split('--') || []

        const RenderElement = ENTRY_TYPE_VS_RENDERER[entryType as ENTRY_TYPES]
        if (RenderElement) {
          return <RenderElement innerText={innerText} classNames={classNames} />
        } else {
          const error = new Error(`Unhandled EMBEDDED_ENTRY type used. used ${entryType}`)
          error.name = 'Unhandled EMBEDDED_ENTRY type'

          captureException(error, {
            module: MODULES.UNKNOWN,
            feature: FEATURES.UNHANDLED_RICHTEXT_EMBEDDED_ENTRY,
            extraInfo: {
              entryType,
            },
          })
        }
      }
      return null
    },
  },
}
